import { useRestaurantDetail } from '../hooks/useRestaurantDetail';
import { useLookups } from '../hooks/useLookups';
import type { AppConfig } from '../config';
import { toDisplayCase } from '../lib/displayCase';
import { GradeBadge } from './GradeBadge';
import { PreviousInspectionsTable } from './PreviousInspectionsTable';
import { ViolationsTable } from './ViolationsTable';

interface Props {
  config: AppConfig;
  permitNumber: string;
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

function countViolations(s: string | null): number {
  if (!s || s === '0') return 0;
  return s.split(',').filter((v) => v.trim() !== '').length;
}

export function DetailDrawer({ config, permitNumber }: Props) {
  const { data, isLoading, error } = useRestaurantDetail(config, permitNumber);
  const lookups = useLookups(config);
  const overrides = lookups.data?.overrides ?? [];

  return (
    <div className="space-y-4 px-6 py-4 bg-gray-50">
      {data && (
        <div>
          <h4 className="text-base font-semibold text-gray-900">{toDisplayCase(data.restaurant_name, overrides, 'name')}</h4>
          <p className="mt-1 text-sm text-gray-600">
            {toDisplayCase(data.address, overrides, 'address')}, {data.city_name}, {data.state} {data.zip_code}
          </p>
        </div>
      )}

      <h4 className="font-semibold text-gray-900 text-base">Latest Inspection</h4>

      {isLoading && <p className="text-sm text-gray-500">Loading details…</p>}
      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          Could not load details: {(error as Error).message}
        </p>
      )}

      {data && (
        <>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
            <Row label="Last Inspection Date" value={fmtDate(data.date_current)} />
            <Row label="Establishment Type" value={data.category_name} />
            <Row label="Inspection Type" value={data.inspection_type ?? '—'} />
            <Row
              label="Current Grade"
              value={<GradeBadge code={data.current_grade} size="sm" />}
            />
            <Row label="Number of Violations" value={String(countViolations(data.current_violations))} />
            <Row label="Number of Demerits" value={String(data.current_demerits ?? 0)} />
          </dl>

          {data.current_violations_resolved.length > 0 && (
            <div className="pt-2">
              <ViolationsTable violations={data.current_violations_resolved} />
            </div>
          )}

          {data.previous_inspections.length > 0 && (
            <div className="pt-4 border-t border-gray-300">
              <h4 className="font-semibold text-gray-900 text-base mb-3">Previous Inspections</h4>
              <PreviousInspectionsTable rows={data.previous_inspections} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-gray-600">{label}:</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
